import {
  Component,
  Element,
  h,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import { devMode } from "../../services/debug";

import { HeaderInterface } from "../../services/header-interface/header-interface.service";
import { Bundle, bundleForLanguage } from "../../services/i18n";
import {
  NotificationResponse,
  notificationService,
} from "../../services/notification/notification.service";
import { AdobeAnalyticsTrackingService } from "../../services/tracking/adobe-analytics-tracking.service";
import { parseTrackingString } from "../../services/tracking/tracking-utils";

import { CustomerState, isRes, TLanguage } from "../../types";
import { TRACKING_CATEGORY } from "./consts";
import { NotificationList } from "./NotificationList";

const ICON = "icon-bell";

const SLOT_DATASET = {
  trackLocation: "header",
  trackAction: "open",
  trackCategory: "l1-icon",
  trackLabel: "notification",
};

@Component({
  tag: "scs-notifications",
  styleUrls: ["notifications.scss"],
  shadow: true,
})
export class Notifications {
  private adobeAnalyticsTrackingService: AdobeAnalyticsTrackingService;
  private i18n!: Bundle;
  private i18nEnglish!: Bundle;
  private sdxHeader!: HeaderInterface;
  private trackByEvent: (event: Event) => void;

  @State()
  private notifications?: NotificationResponse;

  @Element()
  private host!: HTMLElement;

  @Prop()
  public language = (document.documentElement.lang || "de") as TLanguage;

  @Prop()
  public customer: CustomerState = "LOADING";

  public constructor() {
    this.adobeAnalyticsTrackingService =
      AdobeAnalyticsTrackingService.getInstance();
    this.trackByEvent = this.adobeAnalyticsTrackingService.trackByEvent.bind(
      this.adobeAnalyticsTrackingService,
    );
  }

  @Watch("customer")
  @Method() // Solely for mocking
  public async loadNotifications(): Promise<void> {
    const slotName = this.host.slot!;
    const customer = this.customer;
    if (isRes(customer)) {
      try {
        // Set notifications to loading state
        this.sdxHeader.updateSlot(slotName, {
          label: this.i18n.t("NOTIFICATIONS"),
          dataset: SLOT_DATASET,
          iconName: ICON,
        });
        this.notifications = await notificationService.loadNotifications(
          customer.scn,
          customer.segment,
          this.i18n.lang,
        );
        const count = this.notifications.items?.length ?? 0;
        this.sdxHeader.updateSlot(slotName, {
          badge: count > 0 ? String(count) : undefined,
        });
      } catch (e: unknown) {
        if (devMode) {
          console.error("Error loading notifications", e, "hiding the icon");
        }
        this.sdxHeader.updateSlot(slotName);
      }
    } else {
      // Hide notifications for customers not logged in
      this.sdxHeader.updateSlot(slotName);
    }
  }

  public connectedCallback() {
    this.sdxHeader = new HeaderInterface(this.host.closest("sdx-header")!);
  }

  public async componentWillLoad(): Promise<void> {
    if (!this.host.slot) {
      this.host.slot = "notifications";
    }
    [this.i18n, this.i18nEnglish] = await Promise.all([
      bundleForLanguage(this.language),
      bundleForLanguage("en"),
    ]);
    void this.loadNotifications();
  }

  public async componentWillUpdate(): Promise<void> {
    if (this.language !== this.i18n.lang) {
      this.i18n = await bundleForLanguage(this.language);
      const customer = this.customer;
      if (isRes(customer)) {
        this.sdxHeader.updateSlot(this.host.slot!, {
          label: this.i18n.t("NOTIFICATIONS"),
        });
      }
    }
  }

  public render() {
    const notifications = this.notifications;
    if (!notifications) {
      return (
        <div class="container inlay notifications">
          <div class="col-md-12 activities--loading">
            <p>
              <sdx-icon icon-name="icon-hourglass" size={3}></sdx-icon>
            </p>
          </div>
        </div>
      );
    }
    return (
      <div class="container inlay notifications">
        {notifications.items?.length ? (
          <NotificationList
            {...notifications}
            i18n={this.i18n}
            i18nEnglish={this.i18nEnglish}
            trackByEvent={this.trackByEvent}
          />
        ) : (
          <div class="col-md-12 no-activities-message">
            <i class="icon icon-086-thumb-up icon--s4" aria-hidden="true" />
            <p>
              {this.i18n.t("NOTIFICATIONS_NO_NOTIFICATION_MESSAGE_ONE")}
              <br />
              {this.i18n.t("NOTIFICATIONS_NO_NOTIFICATION_MESSAGE_TWO")}
            </p>

            <a
              class="link"
              href="/myswisscom/activities"
              onClick={this.trackByEvent}
              title={this.i18n.t("NOTIFICATIONS_SHOW_ALL_ACTIVITIES")}
              data-tracking-category={TRACKING_CATEGORY}
              data-tracking-label={
                "notification-" +
                parseTrackingString(
                  this.i18nEnglish.t("NOTIFICATIONS_SHOW_ALL_ACTIVITIES"),
                )
              }
            >
              {this.i18n.t("NOTIFICATIONS_SHOW_ALL_ACTIVITIES")}
            </a>
          </div>
        )}
      </div>
    );
  }
}
